// src/components/teacher/TeacherStudents.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL, getAuthHeaders } from "../../utils/api";
import { SearchIcon, StudentsIcon } from "../shared/icons/Icons";
import { USE_DUMMY_DATA, DUMMY_STUDENTS } from "./dummyData";

function TeacherStudents({ courses, loading: parentLoading }) {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [classFilter, setClassFilter] = useState("");

  useEffect(() => {
    const fetchStudents = async () => {
      if (USE_DUMMY_DATA) {
        setStudents(DUMMY_STUDENTS);
        setLoading(false);
        return;
      }

      try {
        const params = new URLSearchParams();
        if (classFilter) params.set("class_name", classFilter);

        const response = await fetch(
          `${API_BASE_URL}/api/teachers/me/students?${params.toString()}`,
          {
            headers: {
              ...getAuthHeaders(),
              "Content-Type": "application/json",
            },
          },
        );

        if (!response.ok) {
          throw new Error("Failed to fetch students");
        }

        const data = await response.json();
        setStudents(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to fetch students:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, [classFilter]);

  const uniqueClasses = [
    ...new Set(students.map((s) => s.class_name).filter(Boolean)),
  ].sort();

  const filteredStudents = students.filter((student) => {
    const matchesSearch =
      student.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.student_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.father_name?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCourse =
      selectedCourse === "all" ||
      student.course_id === parseInt(selectedCourse, 10);

    const matchesClass =
      !classFilter || student.class_name === classFilter;

    return matchesSearch && matchesCourse && matchesClass;
  });

  const stats = {
    total: students.length,
    active: students.filter((s) => s.student_status === "Active").length,
    withdrawn: students.filter((s) => s.student_status === "Withdrawn").length,
  };

  const courseNameById = new Map(
    courses.map((course) => [
      String(course.course_id || course.id),
      course.course_name || course.name,
    ]),
  );

  const getCourseName = (student) => {
    return (
      student.course_name ||
      courseNameById.get(String(student.course_id || "")) ||
      "N/A"
    );
  };

  if (parentLoading || loading) {
    return (
      <div className="tp-loading">
        <div className="tp-spinner" />
        <p>Loading your students...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="tp-page-header">
        <div>
          <h2>
            <StudentsIcon />
            My Students
          </h2>
          <p>Students assigned to your classes — click a row to enter grades</p>
        </div>
        <span className="tp-badge">{students.length} Total Students</span>
      </div>

      <div className="tp-stats-grid">
        <button
          type="button"
          className="tp-card tp-card--clickable"
          onClick={() => setClassFilter("")}
        >
          <h3 className="tp-stat-value">{stats.total}</h3>
          <p className="tp-stat-label">Total Students</p>
        </button>
        <div className="tp-card">
          <h3 className="tp-stat-value" style={{ color: "#16a34a" }}>
            {stats.active}
          </h3>
          <p className="tp-stat-label">Active</p>
        </div>
        <div className="tp-card">
          <h3 className="tp-stat-value" style={{ color: "#dc2626" }}>
            {stats.withdrawn}
          </h3>
          <p className="tp-stat-label">Withdrawn</p>
        </div>
        <button
          type="button"
          className="tp-card tp-card--clickable"
          onClick={() => navigate("/teacher/courses")}
        >
          <h3 className="tp-stat-value" style={{ color: "#7c3aed" }}>
            {courses.length}
          </h3>
          <p className="tp-stat-label">My Courses</p>
        </button>
      </div>

      <div className="tp-filters">
        <select
          className="tp-select"
          value={classFilter}
          onChange={(e) => setClassFilter(e.target.value)}
        >
          <option value="">All Classes</option>
          {uniqueClasses.map((cls) => (
            <option key={cls} value={cls}>
              {cls}
            </option>
          ))}
        </select>

        {courses.length > 0 ? (
          <select
            className="tp-select"
            value={selectedCourse}
            onChange={(e) => setSelectedCourse(e.target.value)}
          >
            <option value="all">All Courses</option>
            {/* Dedupe by course_id — a teacher may teach the same course to
                several classes, but the filter matches on course_id. */}
            {Array.from(
              new Map(courses.map((c) => [c.course_id, c])).values(),
            ).map((course) => (
              <option key={course.course_id} value={course.course_id}>
                {course.course_name || course.name}
              </option>
            ))}
          </select>
        ) : null}

        <div className="tp-search">
          <SearchIcon />
          <input
            type="text"
            placeholder="Search by name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {filteredStudents.length > 0 ? (
        <div className="tp-table-wrap">
          <table className="tp-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Father's Name</th>
                <th>Class</th>
                <th>Section</th>
                <th>Course</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.map((student) => (
                <tr
                  key={student.id}
                  className="tp-row-clickable"
                  onClick={() =>
                    navigate(`/teacher/students/${student.id}`, { state: { student } })
                  }
                >
                  <td>
                    <span className="tp-mono">{student.student_id}</span>
                  </td>
                  <td style={{ fontWeight: 600 }}>{student.name}</td>
                  <td>{student.father_name || "N/A"}</td>
                  <td>
                    <span className="tp-chip tp-chip--class">
                      {student.class_name || "N/A"}
                    </span>
                  </td>
                  <td>
                    <span className="tp-chip tp-chip--section">
                      {student.class_section || "N/A"}
                    </span>
                  </td>
                  <td>{getCourseName(student)}</td>
                  <td>
                    <span
                      className={`tp-status ${
                        student.student_status === "Active"
                          ? "tp-status--active"
                          : "tp-status--withdrawn"
                      }`}
                    >
                      {student.student_status || "Active"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="tp-empty">
          <StudentsIcon />
          <h3>No students found</h3>
          <p>
            {searchTerm || classFilter
              ? "No students match your filters."
              : "No students assigned to your classes yet."}
          </p>
        </div>
      )}
    </div>
  );
}

export default TeacherStudents;
